import React from 'react';
import { Controller } from 'react-hook-form';
import { ChevronDown } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { FormField, Input, Select, Textarea } from '../../../components/ui/FormComponents';
import { INSTALLATION_TRADES } from '../utils/teamMemberLabels';
import { useEmployeeForm } from '../hooks/useEmployeeForm';
import { Toast } from '../../../components/ui/Toast';

const CustomRoleSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = [
    { value: "admin", label: "Admin" },
    { value: "project-manager", label: "Project Manager" },
    { value: "field_officer", label: "Installation Member" },
    { value: "accountant", label: "Accountant" },
    { value: "store-manager", label: "Store Incharge" },
    { value: "telecaller", label: "Telecaller" },
    { value: "designer", label: "Designer" },
    { value: "sales_executive", label: "Sales Executive" },
  ];

  const selectedLabel = options.find(o => o.value === value)?.label || "Select Role";

  return (
    <div className="relative w-full" ref={containerRef}>
      <div 
        className="w-full h-[36px] px-3 text-[12px] border border-slate-200 rounded-md text-[13px] text-slate-800 bg-white cursor-pointer flex items-center justify-between shadow-sm focus:outline-none focus:border-slate-800 transition-all"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-[100] top-[calc(100%+4px)] left-0 w-full bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto max-h-[210px] animate-in fade-in zoom-in-95 duration-100">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`px-3 py-2 text-[12px] cursor-pointer hover:bg-slate-50 transition-colors ${value === opt.value ? 'bg-slate-50 font-bold text-slate-900' : 'text-slate-700'}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AddEmployeeModal = ({ isOpen, onClose, onSubmit, isLoading, initialData }) => {
  const {
    register,
    submit,
    errors,
    role,
    locations,
    isEdit,
    control
  } = useEmployeeForm({ isOpen, initialData, onSubmit });

  const [toast, setToast] = React.useState(null);
  const todayDate = new Date().toISOString().split('T')[0];

  const onInvalid = (formErrors) => {
    const first = Object.values(formErrors || {})?.[0];
    const msg = first?.message || 'Please fix the highlighted fields.';
    setToast({ message: msg, type: 'error' });
  };

  // Shared classes for elegant high-contrast elements inside white modal
  const inputClass = "text-[12px] font-medium text-slate-800 bg-white border-slate-200 focus:border-slate-800 transition-all h-[36px] shadow-sm rounded-md";
  const fieldLabel = (text, req = false) => (
    <span className="text-slate-700 font-bold text-[12px] tracking-wide uppercase">
      {text} {req && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );

  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title={
          <div className="flex flex-col gap-0.5 z-10 relative">
            <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
              VERVE KITCHENS
            </span>
            <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
              {isEdit ? 'Modify Crew Profile' : 'Crew Onboarding Panel'}
            </span>
          </div>
        }
        size="lg"
      >
        {/* Form fields directly laid out on a clean white background */}
        <form onSubmit={submit(onInvalid)} className="flex flex-col gap-5">
          {/* Row 1: First Name & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FormField label={fieldLabel('First Name', true)} error={errors.firstName} field="firstName">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input id="firstName" className={inputClass} placeholder="First name" {...field} />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Last Name')} error={errors.lastName} field="lastName">
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input id="lastName" className={inputClass} placeholder="Last name" {...field} />
                )}
              />
            </FormField>
          </div>

          {/* Row 2: Phone & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FormField label={fieldLabel('Phone Number', true)} error={errors.phone} field="phone">
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input id="phone" className={inputClass} placeholder="10 digit mobile" {...field} maxLength={10} inputMode="numeric" />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Email Address', true)} error={errors.email} field="email">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input id="email" className={inputClass} placeholder="email@verve.com" type="email" {...field} />
                )}
              />
            </FormField>
          </div>

          {/* Row 3: Employee ID & Role */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FormField label={fieldLabel('Employee ID', true)} error={errors.employeeId} field="employeeId">
              <Controller
                name="employeeId"
                control={control}
                render={({ field }) => (
                  <Input
                    id="employeeId"
                    className={inputClass}
                    placeholder="EMP001"
                    {...field}
                  />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Role Assignment', true)} error={errors.role} field="role">
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <CustomRoleSelect value={field.value} onChange={field.onChange} />
                )}
              />
            </FormField>
          </div>

          {/* Dynamic Grid Pairing depending on whether Role Type (installation member) is active */}
          {role === 'field_officer' ? (
            <>
              {/* Row 4: Role Type & Joining Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField label={fieldLabel('Role Type', true)} error={errors.installationTrade}>
                  <Controller
                    name="installationTrade"
                    control={control}
                    render={({ field }) => (
                      <Select className={`${inputClass} pr-10`} {...field}>
                        <option value="">Select type</option>
                        {INSTALLATION_TRADES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>

                <FormField label={fieldLabel('Joining Date', true)} error={errors.joiningDate} field="joiningDate">
                  <Controller
                    name="joiningDate"
                    control={control}
                    render={({ field }) => (
                      <Input id="joiningDate" className={`${inputClass} cursor-pointer`} type="date" max={todayDate} {...field} />
                    )}
                  />
                </FormField>
              </div>

              {/* Row 5: Work Location & Login User ID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField label={fieldLabel('Work Location', true)} error={errors.workLocation} field="workLocation">
                  <Controller
                    name="workLocation"
                    control={control}
                    render={({ field }) => (
                      <Select id="workLocation" className={`${inputClass} pr-10`} {...field}>
                        <option value="">Select office location</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc.name}>
                            {loc.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>

                <FormField label={fieldLabel('Login User ID (optional)')} error={errors.loginId} field="loginId">
                  <Controller
                    name="loginId"
                    control={control}
                    render={({ field }) => (
                      <Input id="loginId" className={inputClass} placeholder="User ID for login" {...field} />
                    )}
                  />
                </FormField>
              </div>

              {/* Row 6: Password (spans full width beautifully when role type is present) */}
              <FormField
                label={fieldLabel(isEdit ? 'New password (optional)' : 'Password', !isEdit)}
                error={errors.password}
                field="password"
              >
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="password"
                      className={inputClass}
                      placeholder={
                        isEdit
                          ? 'Leave blank to keep current'
                          : '8–10 chars: 1 uppercase, 1 special (!@#…)'
                      }
                      type="password"
                      maxLength={10}
                      {...field}
                      autoComplete="new-password"
                    />
                  )}
                />
              </FormField>
            </>
          ) : (
            <>
              {/* Row 4: Joining Date & Work Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField label={fieldLabel('Joining Date', true)} error={errors.joiningDate} field="joiningDate">
                  <Controller
                    name="joiningDate"
                    control={control}
                    render={({ field }) => (
                      <Input id="joiningDate" className={`${inputClass} cursor-pointer`} type="date" max={todayDate} {...field} />
                    )}
                  />
                </FormField>

                <FormField label={fieldLabel('Work Location', true)} error={errors.workLocation} field="workLocation">
                  <Controller
                    name="workLocation"
                    control={control}
                    render={({ field }) => (
                      <Select id="workLocation" className={`${inputClass} pr-10`} {...field}>
                        <option value="">Select office location</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc.name}>
                            {loc.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>
              </div>

              {/* Row 5: Login User ID & Password */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField label={fieldLabel('Login User ID (optional)')} error={errors.loginId} field="loginId">
                  <Controller
                    name="loginId"
                    control={control}
                    render={({ field }) => (
                      <Input id="loginId" className={inputClass} placeholder="User ID for login" {...field} />
                    )}
                  />
                </FormField>

                <FormField
                  label={fieldLabel(isEdit ? 'New password (optional)' : 'Password', !isEdit)}
                  error={errors.password}
                  field="password"
                >
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="password"
                        className={inputClass}
                        placeholder={
                          isEdit
                            ? 'Leave blank to keep current'
                            : '8–10 chars: 1 uppercase, 1 special (!@#…)'
                        }
                        type="password"
                        maxLength={10}
                        {...field}
                        autoComplete="new-password"
                      />
                    )}
                  />
                </FormField>
              </div>
            </>
          )}

          {/* Notes */}
          <FormField label={fieldLabel('Administrative Notes')} error={errors.notes}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea className="text-[12px] font-medium text-slate-800 bg-white border-slate-200 focus:border-slate-800 transition-all font-medium text-[12px] shadow-sm rounded-md" placeholder="Background info or notes..." {...field} rows={2} />
              )}
            />
          </FormField>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-semibold transition-all duration-300 ease-in-out shadow-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed border border-slate-900 shadow-sm"
            >
              {isLoading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Member'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};
